import { useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { MapPicker } from "../components/MapPicker";
import api, { formatApiError } from "../lib/api";
import { uploadToCloudinary } from "../lib/cloudinary";
import { useHoardingTypes } from "../lib/hoardingTypes";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { hasPermission } from "../lib/permissions";
import { toast } from "sonner";
import { Upload, X, ArrowLeft, Save, Plus } from "lucide-react";

const EMPTY = {
  hoarding_id: "", auto_generate_id: true, name: "", type: "",
  city: "", area: "", address: "", latitude: null, longitude: null,
  width: 20, height: 10, facing: "North",
  lighting: false, monthly_price: 25000, monthly_rent: 0,
  default_printing_charge: 0, default_installation_charge: 0,
  photos: [], notes: "",
  landmark: "",
  proposal_city_label: "", proposal_route_segment: "City", proposal_direction: "na",
};

const LOCATION_TYPES = ["City", "Highway", "Bypass", "Entry Point", "Exit Point", "Airport Road", "Other"];
const DIRECTIONAL_LOCATION_TYPES = new Set(["highway", "bypass", "entry point", "exit point", "airport road"]);

function supportsTrafficDirection(locationType) {
  return DIRECTIONAL_LOCATION_TYPES.has(String(locationType || "").trim().toLowerCase());
}

const PHOTO_VIEW_TYPES = [
  { value: "day_close_view", label: "Daytime Close View" },
  { value: "day_wide_view", label: "Daytime Wide View" },
  { value: "night_close_view", label: "Nighttime Close View" },
  { value: "night_wide_view", label: "Nighttime Wide View" },
];

function RequiredLabel({ children, className = "" }) {
  return (
    <Label className={className}>
      {children} <span className="text-blue-500">*</span>
    </Label>
  );
}

function OptionalLabel({ children, className = "" }) {
  return <Label className={className}>{children}</Label>;
}

function numericInputValue(value) {
  return value === null || value === undefined ? "" : value;
}

function parseNumberInput(value) {
  return value === "" ? "" : parseFloat(value);
}

export function HoardingForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitMode, setSubmitMode] = useState("done");
  const [uploading, setUploading] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [existingHoardings, setExistingHoardings] = useState([]);
  const [cityFocused, setCityFocused] = useState(false);
  const [areaFocused, setAreaFocused] = useState(false);
  const { names: hoardingTypes, loading: loadingHoardingTypes } = useHoardingTypes();
  const canUploadPhotos = hasPermission(user, "upload_hoarding_photos");
  const canEditRent = hasPermission(user, "edit_rent_details");
  const visibleHoardingTypes = useMemo(() => {
    if (!form.type || hoardingTypes.includes(form.type)) return hoardingTypes;
    return [form.type, ...hoardingTypes];
  }, [form.type, hoardingTypes]);

  useEffect(() => {
    api.get("/hoardings/cities", { skipGlobalLoader: true })
      .then((r) => setCitySuggestions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCitySuggestions([]));
    api.get("/hoardings", { skipGlobalLoader: true })
      .then((r) => setExistingHoardings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExistingHoardings([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/hoardings/${id}`, { skipGlobalLoader: true }).then((r) => {
      const d = r.data;
      setForm({
        ...EMPTY, ...d, auto_generate_id: false,
        photos: d.photos || [],
      });
    });
  }, [id]);

  useEffect(() => {
    if (id || !location.state?.template) return;
    setForm({
      ...EMPTY,
      ...location.state.template,
      hoarding_id: "",
      auto_generate_id: true,
      photos: [],
    });
  }, [id, location.state]);

  const sqft = useMemo(() => Math.round((Number(form.width) || 0) * (Number(form.height) || 0) * 100) / 100, [form.width, form.height]);
  const matchingCities = useMemo(() => {
    const query = form.city.trim().toLowerCase();
    if (!query) return citySuggestions.slice(0, 6);
    return citySuggestions.filter((city) => city.toLowerCase().startsWith(query)).slice(0, 6);
  }, [citySuggestions, form.city]);
  const areaSuggestions = useMemo(() => {
    const selectedCity = form.city.trim().toLowerCase();
    if (!selectedCity) return [];
    const seen = new Set();
    return existingHoardings
      .filter((h) => (h.city || "").trim().toLowerCase() === selectedCity)
      .map((h) => (h.area || "").trim())
      .filter(Boolean)
      .filter((area) => {
        const key = area.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  }, [existingHoardings, form.city]);
  const matchingAreas = useMemo(() => {
    const query = form.area.trim().toLowerCase();
    if (!query) return areaSuggestions.slice(0, 6);
    return areaSuggestions.filter((area) => area.toLowerCase().startsWith(query)).slice(0, 6);
  }, [areaSuggestions, form.area]);

  async function uploadFiles(files) {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const r = await uploadToCloudinary(f);
        uploaded.push(r);
      }
      setForm((p) => ({
        ...p,
        photos: [
          ...(p.photos || []),
          ...uploaded.map((photo) => ({ ...photo, view_type: "day_close_view" })),
        ],
      }));
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  }

  function removePhoto(idx) {
    setForm((p) => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!form.area.trim()) {
      toast.error("Area is required");
      return;
    }
    if (!id && !form.type.trim()) {
      toast.error("Add a media type in Business Settings, then select it here.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        city: form.city.trim(),
        area: form.area.trim(),
        lighting: !!form.lighting,
        proposal_city_label: form.proposal_city_label.trim(),
        proposal_route_segment: form.proposal_route_segment.trim() || "City",
        proposal_direction: supportsTrafficDirection(form.proposal_route_segment) ? (form.proposal_direction || "na") : "na",
        width: parseFloat(form.width || 0),
        height: parseFloat(form.height || 0),
        monthly_price: parseFloat(form.monthly_price || 0),
        default_printing_charge: parseFloat(form.default_printing_charge || 0),
        default_installation_charge: parseFloat(form.default_installation_charge || 0),
        monthly_rent: parseFloat(form.monthly_rent || 0),
      };
      if (id) {
        delete payload.hoarding_id;
        delete payload.auto_generate_id;
        delete payload.type;
        delete payload.client_id;
        delete payload.client_name;
        delete payload.client_mobile;
        delete payload.campaign_start;
        delete payload.campaign_end;
        delete payload.campaign_id;
        if (!canEditRent) delete payload.monthly_rent;
        if (!canUploadPhotos) delete payload.photos;
        await api.put(`/hoardings/${id}`, payload);
        toast.success("Hoarding updated");
      } else {
        await api.post("/hoardings", payload);
        toast.success("Hoarding created");
      }
      if (!id && submitMode === "continue") {
        setForm({
          ...EMPTY,
          city: form.city,
          area: form.area,
          address: form.address,
          landmark: form.landmark,
          type: form.type,
          width: form.width,
          height: form.height,
          facing: form.facing,
          lighting: form.lighting,
          monthly_price: form.monthly_price,
          default_printing_charge: form.default_printing_charge,
          default_installation_charge: form.default_installation_charge,
          monthly_rent: form.monthly_rent,
          latitude: form.latitude,
          longitude: form.longitude,
          proposal_city_label: form.proposal_city_label,
          proposal_route_segment: form.proposal_route_segment || "City",
          proposal_direction: supportsTrafficDirection(form.proposal_route_segment) ? form.proposal_direction : "na",
        });
        setSubmitMode("done");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      nav("/hoardings");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout
      title={id ? "Edit Hoarding" : "Add Hoarding"}
      subtitle={id ? "Update inventory item" : "Create a new hoarding entry in your inventory"}
      actions={
        <button onClick={() => nav(-1)} className="hx-secondary-action">
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <form onSubmit={submit} className="mx-auto grid max-w-6xl grid-cols-1 gap-5 pb-2 2xl:grid-cols-[minmax(0,1fr)_360px]" data-testid="hoarding-form">
        <div className="space-y-5">
          <div className="hx-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Identity</h3>
              {!id && (
                <label className="inline-flex items-center gap-2 text-xs">
                  <Switch checked={form.auto_generate_id} onCheckedChange={(c) => setForm({ ...form, auto_generate_id: c })} />
                  Auto-generate ID
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <RequiredLabel>Hoarding ID</RequiredLabel>
                <Input data-testid="hf-id" value={form.hoarding_id} disabled={!!id || (!id && form.auto_generate_id)} onChange={(e) => setForm({ ...form, hoarding_id: e.target.value })} placeholder="PUNE-0001" />
                {id && <p className="text-[10px] text-slate-400">Hoarding ID cannot be changed after creation.</p>}
              </div>
              <div className="space-y-1">
                <RequiredLabel>Name</RequiredLabel>
                <Input data-testid="hf-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <RequiredLabel>Type</RequiredLabel>
                <Select value={form.type} disabled={!!id || hoardingTypes.length === 0} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="hf-type">
                    <SelectValue placeholder={loadingHoardingTypes ? "Loading types..." : "Select type"} />
                  </SelectTrigger>
                  <SelectContent>{visibleHoardingTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                {id && <p className="text-[10px] text-slate-400">Type cannot be changed after creation.</p>}
                {!id && !loadingHoardingTypes && hoardingTypes.length === 0 && (
                  <button type="button" onClick={() => nav("/business")} className="text-left text-[10px] font-semibold text-blue-600 hover:text-blue-700">
                    Add media types in Business Settings first.
                  </button>
                )}
              </div>
              <div className="relative space-y-1">
                <RequiredLabel>City</RequiredLabel>
                <Input
                  data-testid="hf-city"
                  required
                  value={form.city}
                  onFocus={() => setCityFocused(true)}
                  onBlur={() => window.setTimeout(() => setCityFocused(false), 120)}
                  onChange={(e) => setForm({ ...form, city: e.target.value, area: "" })}
                  placeholder="Enter city name"
                  autoComplete="off"
                />
                {cityFocused && matchingCities.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                    {matchingCities.map((city) => (
                      <button
                        key={city}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setForm({ ...form, city, area: "" });
                          setCityFocused(false);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative space-y-1">
                <RequiredLabel>Area</RequiredLabel>
                <Input
                  data-testid="hf-area"
                  required
                  disabled={!form.city.trim()}
                  value={form.area}
                  onFocus={() => setAreaFocused(true)}
                  onBlur={() => window.setTimeout(() => setAreaFocused(false), 120)}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                  placeholder={form.city.trim() ? "Enter area name" : "Select city first"}
                  autoComplete="off"
                />
                {areaFocused && form.city.trim() && matchingAreas.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                    {matchingAreas.map((area) => (
                      <button
                        key={area}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setForm({ ...form, area });
                          setAreaFocused(false);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <RequiredLabel>Landmark</RequiredLabel>
                <Input value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <RequiredLabel>Address</RequiredLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="hx-card p-6 space-y-4">
            <h3 className="font-display font-semibold text-slate-900">Specifications</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <RequiredLabel>Width (ft)</RequiredLabel>
                <Input data-testid="hf-width" type="number" step="0.1" value={numericInputValue(form.width)} onChange={(e) => setForm({ ...form, width: parseNumberInput(e.target.value) })} />
              </div>
              <div className="space-y-1">
                <RequiredLabel>Height (ft)</RequiredLabel>
                <Input data-testid="hf-height" type="number" step="0.1" value={numericInputValue(form.height)} onChange={(e) => setForm({ ...form, height: parseNumberInput(e.target.value) })} />
              </div>
              <div className="space-y-1">
                <RequiredLabel>Sqft (auto)</RequiredLabel>
                <Input value={sqft} disabled />
              </div>
              <div className="space-y-1">
                <RequiredLabel>Facing</RequiredLabel>
                <Select value={form.facing} onValueChange={(v) => setForm({ ...form, facing: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["North", "South", "East", "West", "NE", "NW", "SE", "SW"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <div className="space-y-1 w-full">
                  <RequiredLabel>Illumination</RequiredLabel>
                  <Select value={form.lighting ? "lighted" : "non_lighted"} onValueChange={(v) => setForm({ ...form, lighting: v === "lighted" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lighted">Lighted</SelectItem>
                      <SelectItem value="non_lighted">Non-lighted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="hx-card p-6 space-y-4">
            <div>
              <h3 className="font-display font-semibold text-slate-900">Proposal Arrangement</h3>
              <p className="mt-1 text-xs text-slate-500">Optional labels used only for proposal grouping and custom flow ordering.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <OptionalLabel>Proposal Market</OptionalLabel>
                <Input value={form.proposal_city_label} onChange={(e) => setForm({ ...form, proposal_city_label: e.target.value })} placeholder={form.city || "Sangli"} />
              </div>
              <div className="space-y-1">
                <OptionalLabel>Location Type</OptionalLabel>
                <Input
                  list="proposal-location-types"
                  value={form.proposal_route_segment}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm({ ...form, proposal_route_segment: next, proposal_direction: supportsTrafficDirection(next) ? form.proposal_direction : "na" });
                  }}
                  placeholder="City"
                />
                <datalist id="proposal-location-types">
                  {LOCATION_TYPES.map((segment) => <option key={segment} value={segment} />)}
                </datalist>
              </div>
              <div className="space-y-1">
                <OptionalLabel>Traffic Direction</OptionalLabel>
                <Select value={form.proposal_direction || "na"} disabled={!supportsTrafficDirection(form.proposal_route_segment)} onValueChange={(v) => setForm({ ...form, proposal_direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upstream">Upstream</SelectItem>
                    <SelectItem value="downstream">Downstream</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="na">NA</SelectItem>
                  </SelectContent>
                </Select>
                {!supportsTrafficDirection(form.proposal_route_segment) && <p className="text-[10px] text-slate-400">Direction applies only to highway/bypass-style locations.</p>}
              </div>
            </div>
          </div>

          <div className="hx-card p-6 space-y-4">
            <h3 className="font-display font-semibold text-slate-900">Booking & Pricing</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <RequiredLabel>Monthly Price (₹)</RequiredLabel>
                <Input data-testid="hf-price" type="number" value={numericInputValue(form.monthly_price)} onChange={(e) => setForm({ ...form, monthly_price: parseNumberInput(e.target.value) })} />
                <p className="text-[10px] text-slate-400">Revenue billed to client</p>
              </div>
              <div className="space-y-1">
                <OptionalLabel>Default Printing (₹)</OptionalLabel>
                <Input type="number" min="0" value={numericInputValue(form.default_printing_charge)} onChange={(e) => setForm({ ...form, default_printing_charge: parseNumberInput(e.target.value) })} placeholder="0" />
                <p className="text-[10px] text-slate-400">Used if campaign printing is blank</p>
              </div>
              <div className="space-y-1">
                <OptionalLabel>Default Installation (₹)</OptionalLabel>
                <Input type="number" min="0" value={numericInputValue(form.default_installation_charge)} onChange={(e) => setForm({ ...form, default_installation_charge: parseNumberInput(e.target.value) })} placeholder="0" />
                <p className="text-[10px] text-slate-400">Used if campaign install is blank</p>
              </div>
              {canEditRent && <div className="space-y-1">
                <RequiredLabel>Monthly Rent (₹)</RequiredLabel>
                <Input data-testid="hf-rent" type="number" value={numericInputValue(form.monthly_rent) === 0 ? "" : numericInputValue(form.monthly_rent)} onChange={(e) => setForm({ ...form, monthly_rent: parseNumberInput(e.target.value) })} placeholder="0" />
                <p className="text-[10px] text-slate-400">Rent paid to property owner</p>
              </div>}
            </div>
            <div className="space-y-1">
              <OptionalLabel>Notes</OptionalLabel>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {canUploadPhotos && <div className="hx-card p-6 space-y-3">
            <h3 className="font-display font-semibold text-slate-900">Photos</h3>
            <label className="block border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-red-300 transition" data-testid="hf-upload-zone">
              <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
              <Upload size={20} className="mx-auto text-slate-400" />
              <div className="text-sm text-slate-600 mt-2">{uploading ? "Uploading…" : "Click to upload to Cloudinary"}</div>
              <div className="text-xs text-slate-400 mt-1">JPG/PNG, multiple supported</div>
            </label>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {form.photos.map((p, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-white">
                    <img src={p.url} alt="" className="w-full h-24 object-cover" />
                    <div className="p-1.5">
                      <Select value={p.view_type || "day_close_view"} onValueChange={(v) => setForm((prev) => ({
                        ...prev,
                        photos: prev.photos.map((photo, i) => i === idx ? { ...photo, view_type: v } : photo),
                      }))}>
                        <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PHOTO_VIEW_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition" data-testid={`remove-photo-${idx}`}>
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>}
        </div>

        {/* right column - map & save */}
        <div className="space-y-5 self-start 2xl:sticky 2xl:top-5">
          <div className="hx-card p-6">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Location on Map</h3>
            <MapPicker
              value={{ lat: form.latitude, lng: form.longitude }}
              onChange={({ lat, lng }) => setForm({ ...form, latitude: lat, longitude: lng })}
              height={300}
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="space-y-1">
                <RequiredLabel className="text-xs">Latitude</RequiredLabel>
                <Input type="number" step="0.000001" value={form.latitude ?? ""} onChange={(e) => setForm({ ...form, latitude: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
              <div className="space-y-1">
                <RequiredLabel className="text-xs">Longitude</RequiredLabel>
                <Input type="number" step="0.000001" value={form.longitude ?? ""} onChange={(e) => setForm({ ...form, longitude: e.target.value === "" ? null : parseFloat(e.target.value) })} />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Click anywhere on the map to drop a pin.</p>
          </div>
          <div className="space-y-2">
            <button type="submit" onClick={() => setSubmitMode("done")} disabled={loading} data-testid="hf-save" className="w-full py-3 rounded-lg bg-blue-600 hover:bg-blue-700 hover:-translate-y-px text-white font-semibold inline-flex items-center justify-center gap-2 shadow-sm shadow-blue-200 transition-all duration-150 disabled:opacity-60 disabled:translate-y-0">
              <Save size={16} /> {loading ? "Saving…" : id ? "Update Hoarding" : "Create Hoarding"}
            </button>
            {!id && (
              <button type="submit" onClick={() => setSubmitMode("continue")} disabled={loading} className="w-full py-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 font-semibold inline-flex items-center justify-center gap-2 hover:bg-red-100 transition-colors disabled:opacity-50">
                <Plus size={16} /> Save and add another
              </button>
            )}
          </div>
        </div>
      </form>
    </Layout>
  );
}
